<template>
  <form @submit.prevent="nextStep">
    <div class="form-group registration-steps">
      <span class="badge badge-warning p-2">Stap 1 - Bedrijfsgegevens</span>
      - - -
      <span class="badge badge-secondary p-2">2</span>
      - - -
      <span class="badge badge-secondary p-2">3</span>
    </div>

    <div class="form-group">
      <label for="coc_number">KvK-nummer</label>&nbsp;
      <font-awesome-icon class="font-awesome-icon" v-if="looking_for_company" icon="spinner" spin/>
      <input
        id="coc_number"
        type="text"
        class="form-control"
        maxlength="8"
        :class="{'is-invalid': $v.coc_number.$error, 'is-valid': !$v.coc_number.$error && $v.coc_number.$dirty}"
        v-model="coc_number"
        @blur="$v.coc_number.$touch()"
      >
      <span v-if="$v.coc_number.$error" class="invalid-feedback" role="alert">
        <strong>Vul a.u.b. het 8 cijferige KvK-nummer in.</strong>
      </span>
    </div>

    <div v-if="could_not_find_company" class="form-group">
      <div class="alert alert-warning" role="alert">
        Helaas geen bedrijf met KvK-nummer: "{{ coc_number }}" kunnen vinden, vul a.u.b. de onderstaande gegevens in.
      </div>
    </div>

    <div class="form-group">
      <label for="company_name">Bedrijfsnaam</label>
      <input
        id="company_name"
        type="text"
        class="form-control"
        :class="{'is-invalid': $v.company_name.$error, 'is-valid': !$v.company_name.$error && $v.company_name.$dirty}"
        :disabled="disable_fields"
        v-model="company_name"
        @blur="$v.company_name.$touch()"
      >
      <span v-if="$v.company_name.$error" class="invalid-feedback" role="alert">
        <strong>Vul a.u.b. de bedrijfsnaam in.</strong>
      </span>
    </div>

    <div class="form-group">
      <label for="company_address">Adres</label>
      <input
        id="company_address"
        type="text"
        class="form-control"
        :class="{'is-invalid': $v.company_address.$error, 'is-valid': !$v.company_address.$error && $v.company_address.$dirty}"
        :disabled="disable_fields"
        v-model="company_address"
        @blur="$v.company_address.$touch()"
      >
      <span v-if="$v.company_address.$error" class="invalid-feedback" role="alert">
        <strong>Vul a.u.b. het adres in.</strong>
      </span>
    </div>

    <div class="form-row form-group">
      <div class="col-md-5">
        <label for="company_postcode" class="col-form-label">Postcode</label>
        <input
          id="company_postcode"
          name="company_postcode"
          type="text"
          class="form-control"
          :class="{'is-invalid': $v.company_postcode.$error, 'is-valid': !$v.company_postcode.$error && $v.company_postcode.$dirty}"
          :disabled="disable_fields"
          v-model="company_postcode"
          @blur="$v.company_postcode.$touch()"
          placeholder="1234AB"
        >
        <span v-if="$v.company_postcode.$error" class="invalid-feedback" role="alert">
          <strong>Vul a.u.b. de postcode in.</strong>
        </span>
      </div>
      <div class="col-md-8">
        <label for="company_city" class="col-form-label">Plaats</label>
        <input
          id="company_city"
          name="company_city"
          type="text"
          class="form-control"
          :class="{'is-invalid': $v.company_city.$error, 'is-valid': !$v.company_city.$error && $v.company_city.$dirty}"
          :disabled="disable_fields"
          v-model="company_city"
          @blur="$v.company_city.$touch()"
          placeholder="Plaats"
        >
        <span v-if="$v.company_city.$error" class="invalid-feedback" role="alert">
          <strong>Vul a.u.b. de plaats in.</strong>
        </span>
      </div>
    </div>

    <div class="form-group mt-2">
      <label for="tax_number">BTW-nummer <span class="comment">(Optioneel)</span></label>
      <input
        id="tax_number"
        type="text"
        class="form-control"
        :class="{'is-invalid': $v.tax_number.$error, 'is-valid': !$v.tax_number.$error && $v.tax_number.$dirty}"
        :disabled="disable_fields"
        v-model="tax_number"
        @blur="$v.tax_number.$touch()"
      >
    </div>

    <div class="row justify-content-center mt-5">
      <div class="col-md-13">
        <button
          type="submit"
          class="btn btn-oldevuuren w-100 btn-round"
          :disabled="$v.$invalid"
          :class="{'disabled cursor-not-allowed': $v.$invalid}"
        >
          Naar persoonsgegevens
        </button>
      </div>

      <div class="row align-content-center mt-2">
        <div class="col-md-13">
          <span class="comment">Al een account?</span>&nbsp;<span @click="login" class="link underline">Log in</span>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
  import axios from 'axios'
  import { maxLength, minLength, required } from 'vuelidate/lib/validators'
  import { SAVE_REGISTRATION_DATA } from '../../../store/types'

  export default {
    props: ['loginParent', 'nextStepParent'],
    data() {
      return {
        coc_number: '',
        tax_number: '',
        company_name: '',
        company_address: '',
        company_postcode: '',
        company_city: '',
        disable_fields: true,
        could_not_find_company: false,
        looking_for_company: false
      }
    },
    validations: {
      coc_number: {
        required,
        minLen: minLength(8),
        maxLen: maxLength(8)
      },
      company_name: {
        required,
        minLen: minLength(2)
      },
      company_address: {
        required,
        minLen: minLength(2)
      },
      company_postcode: {
        required,
        minLen: minLength(6)
      },
      company_city: {
        required,
        minLen: minLength(2)
      },
      tax_number: {}
    },
    watch: {
      coc_number() {
        if (this.coc_number.length === 8) {
          // Default values
          this.could_not_find_company = false
          this.disable_fields = true
          this.looking_for_company = true

          // Make the kvk lookup requests
          axios.post('/customer/lookup', {
            coc_number: this.coc_number
          })
            .then((response) => {
              if (!Array.isArray(response.data)) {
                // Populate our fields
                this.company_name = response.data.handelsnaam

                // Because of string concatenation we need to check for these values, otherwise we'll see 'undefined'
                if (response.data.straat && response.data.huisnummer) {
                  this.company_address = response.data.straat + ' ' + response.data.huisnummer
                }
                this.company_postcode = response.data.postcode
                this.company_city = response.data.plaats
                if (response.data.btw) {
                  this.tax_number = response.data.btw
                }
                // Validate all the fields
                this.$v.$touch()
              } else {
                // Show error
                this.could_not_find_company = true
              }
            })
            .catch((error) => {
              // Should not really happen
              console.log(error)
            })
            .finally(() => {
              // Free fields
              this.disable_fields = false
              this.looking_for_company = false
            })
        }
      }
    },
    methods: {
      login() {
        this.loginParent()
      },
      nextStep() {
        // Save registration data
        this.$store.dispatch(SAVE_REGISTRATION_DATA, {
          key: 'companyDetails',
          formData: {
            coc_number: this.coc_number,
            tax_number: this.tax_number,
            company_name: this.company_name,
            company_address: this.company_address,
            company_postcode: this.company_postcode,
            company_city: this.company_city
          }
        })

        this.nextStepParent()
      }
    }
  }
</script>

<style lang="scss" scoped>
</style>
